import "~/css/redesign-v1/tokens.css";
import "~/common/redesign-v1/base.css";
import "~/common/redesign-v1/fonts.css";
import "./doctor-consents.css";

import * as Dialog from "@radix-ui/react-dialog";
import React, { type ReactNode, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import { isMatching, P } from "ts-pattern";

import { ZIndex } from "~/common/constants";
import { fetchWithAuth } from "~/common/fetch";
import { Button } from "~/common/redesign-v1/ui/button";
import { Checkbox } from "~/common/redesign-v1/ui/checkbox";
import { DialogDescription, DialogTitle } from "~/common/redesign-v1/ui/dialog";
import { useOptionalUser } from "~/common/user";

type ConsentStatus = {
  required: boolean;
  confirmed_at: string | null;
  personal_data: boolean | null;
  marketing: boolean | null;
};

type ConsentValues = { personal_data: boolean; marketing: boolean };

const endpoint = `${process.env.API}/doctor/consents`;

async function requestConsents(path = "", body?: ConsentValues | Record<string, never>): Promise<ConsentStatus> {
  const response = await fetchWithAuth(`${endpoint}${path}`, body ? { method: "POST", body: JSON.stringify(body) } : {});
  if (!response.ok) throw new Error("Consent request failed");
  const value: unknown = await response.json();
  if (
    !isMatching(
      {
        required: P.boolean,
        confirmed_at: P.union(P.string, null),
        personal_data: P.union(P.boolean, null),
        marketing: P.union(P.boolean, null),
      },
      value,
    )
  )
    throw new Error("Invalid consent response");
  return value;
}

type ConsentGateProps = { children: ReactNode; afterConfirmed?: ReactNode };

export function DoctorConsentGate({ children, afterConfirmed }: ConsentGateProps) {
  const user = useOptionalUser();
  const { pathname } = useLocation();
  const publicPage = pathname.startsWith("/viewer/plans/") || pathname.startsWith("/patient/area/");
  const accountId = publicPage ? undefined : user?.account_id;

  // Remount on account changes so one doctor's consent cannot unlock another account.
  return accountId ? (
    <AccountConsentGate key={accountId} afterConfirmed={afterConfirmed}>
      {children}
    </AccountConsentGate>
  ) : (
    <>
      {children}
      {afterConfirmed}
    </>
  );
}

function AccountConsentGate({ children, afterConfirmed }: ConsentGateProps) {
  const [status, setStatus] = useState<ConsentStatus>();
  const [loadError, setLoadError] = useState(false);
  const load = useCallback(async () => {
    try {
      const result = await requestConsents();
      // A status request started before confirmation must not reopen the dialog.
      setStatus((current) => (current?.confirmed_at ? current : result));
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = () => void load();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [load]);

  const blocked = status?.required !== false;
  return (
    <>
      <div
        aria-hidden={blocked || undefined}
        ref={(element) => {
          if (element) element.inert = blocked;
        }}
      >
        {children}
        {!blocked && afterConfirmed}
      </div>
      {!status ? (
        <div data-redesign-v1="" className="doctor-consents-loading" role="status" style={{ zIndex: ZIndex.CENTERED_DIALOG }}>
          <FormattedMessage id={loadError ? "consents.loadError" : "consents.loading"} />
          {loadError && (
            <Button type="button" onClick={() => void load()}>
              <FormattedMessage id="general.retry" />
            </Button>
          )}
        </div>
      ) : status.required ? (
        <ConsentDialog onConfirmed={setStatus} />
      ) : null}
    </>
  );
}

export function ConsentDialog({ onConfirmed }: { onConfirmed: (status: ConsentStatus) => void }) {
  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<ConsentValues>({
    defaultValues: { personal_data: false, marketing: false },
  });
  const [error, setSaveError] = useState(false);
  const personalData = watch("personal_data");

  useEffect(() => {
    // This event is sent only once the dialog is mounted, never on login or status lookup.
    void requestConsents("/shown", {}).catch(() => setSaveError(true));
  }, []);

  const submit = handleSubmit(async (values) => {
    setSaveError(false);
    try {
      const result = await requestConsents("", values);
      if (result.required || !result.confirmed_at || result.personal_data !== true) throw new Error("Consent was not confirmed");
      onConfirmed(result);
    } catch {
      setSaveError(true);
    }
  });

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay data-redesign-v1="" className="doctor-consents-overlay tw-fixed tw-inset-0 tw-bg-backdrop" style={{ zIndex: ZIndex.CENTERED_DIALOG }} />
        <Dialog.Content
          data-redesign-v1=""
          className="doctor-consents-dialog tw-rounded-rd"
          style={{ zIndex: ZIndex.CENTERED_DIALOG + 1 }}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogTitle className="tw-font-rd-accent tw-leading-[30px] tw-text-stone-800">
            <FormattedMessage id="consents.title" />
          </DialogTitle>
          <DialogDescription className="doctor-consents-intro">
            <FormattedMessage
              id="consents.intro"
              values={{
                policy: (chunks) => (
                  <a href="https://3d-smile.ru/policy" target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
              }}
            />
          </DialogDescription>
          <form className="doctor-consents-form" onSubmit={submit} noValidate>
            <div className="doctor-consents-options">
              <div>
                <label className="doctor-consents-option" htmlFor="doctor-consent-personal-data">
                  <Controller
                    name="personal_data"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <Checkbox
                        id="doctor-consent-personal-data"
                        name={field.name}
                        checked={field.value}
                        onChange={(event) => {
                          field.onChange(event);
                          if (event.target.checked) clearErrors("personal_data");
                        }}
                        onBlur={field.onBlur}
                        required
                        aria-required="true"
                        aria-invalid={fieldState.invalid}
                        aria-describedby={fieldState.error ? "doctor-consent-personal-data-error" : undefined}
                        disabled={isSubmitting}
                        className="tw-mt-0.5"
                      />
                    )}
                  />
                  <span>
                    <FormattedMessage
                      id="consents.personal"
                      values={{
                        document: (chunks) => (
                          <a href="https://3d-smile.ru/soglasie-na-obrabotku-personalnyh-dannyh" target="_blank" rel="noopener noreferrer">
                            {chunks}
                          </a>
                        ),
                      }}
                    />{" "}
                    <span aria-hidden="true">*</span>
                  </span>
                </label>
                {errors.personal_data && (
                  <p id="doctor-consent-personal-data-error" role="alert" className="doctor-consents-error doctor-consents-field-error">
                    <FormattedMessage id="consents.personalRequired" />
                  </p>
                )}
              </div>
              <label className="doctor-consents-option" htmlFor="doctor-consent-marketing">
                <Controller
                  name="marketing"
                  control={control}
                  render={({ field }) => (
                    <Checkbox id="doctor-consent-marketing" name={field.name} checked={field.value} onChange={field.onChange} onBlur={field.onBlur} disabled={isSubmitting} className="tw-mt-0.5" />
                  )}
                />
                <span>
                  <FormattedMessage
                    id="consents.marketing"
                    values={{
                      document: (chunks) => (
                        <a href="https://3d-smile.ru/soglasie-na-poluchenie-reklamy" target="_blank" rel="noopener noreferrer">
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </span>
              </label>
            </div>
            <p className="doctor-consents-footnote">
              <FormattedMessage id="consents.footnote" />
            </p>
            {error && (
              <p role="alert" className="doctor-consents-error">
                <FormattedMessage id="consents.saveError" />
              </p>
            )}
            {/* aria-disabled lets invalid click and keyboard attempts show the inline error. */}
            <Button
              size="lg"
              className="doctor-consents-submit tw-w-full"
              type="submit"
              disabled={isSubmitting}
              aria-disabled={!personalData || isSubmitting}
              onClick={(event) => {
                if (!personalData) {
                  event.preventDefault();
                  setError("personal_data", { type: "required" });
                }
              }}
            >
              <FormattedMessage id={isSubmitting ? "consents.saving" : "consents.continue"} />
            </Button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
